import { useContext, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Controller } from "react-hook-form";
import {
    Alert,
    Breadcrumb,
    Button,
    Col,
    Descriptions,
    Empty,
    Flex,
    Form,
    Input,
    Modal,
    Radio,
    Row,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleFilled,
    CustomerServiceOutlined,
    FilePdfOutlined,
    PaperClipOutlined,
    PictureOutlined,
    ReloadOutlined,
    SendOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { IoCheckmarkDone } from "react-icons/io5";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

import useMessageForm from "./useContactForm";
import { ThemeContext } from "../../../../contexts/ThemeContext";
import { ContactStatus, IContact, IMessageContact } from "../../interfaces";
import { formatDateStringToDateAndTime } from "../../../../utils/Conversores";
import CardMessageContact from "../CardMessageContact";
import CardSuggestionMessage from "../CardSuggestionMessage";
import PageHeader from "../../../../components/PageHeader";

const { Text, Paragraph } = Typography;

interface IProps {
    contact?: IContact;
    onSubmitMessage: (formData: FormData) => Promise<void>;
    onHandleStatusChange: (status: ContactStatus) => Promise<void>;
    onRefresh?: () => Promise<void>;
    isRefreshing?: boolean;
}

export default function ContactForm({ contact, onSubmitMessage, onHandleStatusChange, onRefresh, isRefreshing }: IProps) {
    const {
        isSubmiting,
        handleSubmit,
        handleSubmitForm,
        control,
        watchedMessage,
        attachedFiles,
        handleAddFiles,
        handleRemoveFile,
        SUGGESTIONS_RESPONSES,
        handleClickSuggestion,
        handleOpenFile,
        errors,
        isLoadingDelete,
        handleCancelDeleteMessage,
        handleConfirmDeleteMessage,
        isDeleteModalVisible,
        handleDeleteMessage,
        messageContactIdToDelete,
        formIsDisabled,
    } = useMessageForm(onSubmitMessage, contact);

    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const navigate = useNavigate();

    const fileInputRef = useRef<HTMLInputElement>(null);
    const chatContainerRef = useRef<HTMLDivElement>(null);

    const isOpen = contact?.status === ContactStatus.OPEN;

    const allMessages = useMemo(() => {
        const list: (IMessageContact & { isInitial?: boolean })[] = [];
        if (contact?.message || (contact?.files && contact.files.length > 0)) {
            list.push({
                id: 0,
                message: contact.message || "",
                files: contact.files || [],
                app_user_id: contact.app_user?.id,
                app_user: contact.app_user,
                created_at: contact.created_at,
                updated_at: contact.created_at,
                isInitial: true,
            });
        }
        if (contact?.messages && Array.isArray(contact.messages)) {
            list.push(...contact.messages);
        }
        return list;
    }, [contact]);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [allMessages, attachedFiles]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            handleAddFiles(e.target.files);
            e.target.value = "";
        }
    };

    const hasContentToSend = Boolean(
        (watchedMessage && watchedMessage.trim().length > 0) || attachedFiles.length > 0
    );

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div
                style={{
                    background: isDark ? "#141414" : "#ffffff",
                    border: `1px solid ${isDark ? "#262626" : "#f0f0f0"}`,
                    padding: "8px 14px",
                    borderRadius: 8,
                }}
            >
                <Breadcrumb
                    items={[
                        {
                            title: <Link to="/ajuda-e-suporte?tab=usuarios">Ajuda e Suporte</Link>,
                        },
                        {
                            title: `Chamado #${contact?.id ?? "--"}`,
                        },
                    ]}
                />
            </div>
            <PageHeader
                icon={<CustomerServiceOutlined />}
                iconBg="linear-gradient(135deg, #1890ff 0%, #0958d9 100%)"
                title={`Chamado #${contact?.id ?? "--"} - ${contact?.title || "Sem título"}`}
                tag={
                    isOpen ? (
                        <Tag color="warning" icon={<ClockCircleOutlined />}>
                            Aberto
                        </Tag>
                    ) : (
                        <Tag color="success" icon={<CheckCircleOutlined />}>
                            Fechado
                        </Tag>
                    )
                }
                subtitle={`Usuário: ${contact?.app_user?.name || "Usuário"} | Aberto em: ${contact?.created_at ? formatDateStringToDateAndTime(contact.created_at) : "--"}`}
                actions={
                    <Flex align="center" gap={10} wrap="wrap">
                        {onRefresh && (
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={onRefresh}
                                loading={isRefreshing}
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    fontWeight: 500,
                                    borderRadius: 6,
                                }}
                            >
                                Atualizar
                            </Button>
                        )}
                        <Radio.Group
                            value={contact?.status || ContactStatus.OPEN}
                            onChange={(e) => onHandleStatusChange(e.target.value)}
                            buttonStyle="solid"
                            disabled={isSubmiting}
                        >
                            <Radio.Button value={ContactStatus.OPEN}>
                                Aberto
                            </Radio.Button>
                            <Radio.Button
                                value={ContactStatus.CLOSED}
                                style={{
                                    backgroundColor: !isOpen ? "#52c41a" : undefined,
                                    borderColor: !isOpen ? "#52c41a" : undefined,
                                    color: !isOpen ? "#fff" : undefined,
                                }}
                            >
                                Fechado
                            </Radio.Button>
                        </Radio.Group>
                    </Flex>
                }
            />

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={9}>
                    <div
                        style={{
                            background: isDark ? "#1a1a1a" : "#ffffff",
                            border: `1px solid ${isDark ? "#2a2a2a" : "#e2e8f0"}`,
                            boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.04)",
                            borderRadius: 10,
                            padding: "16px 18px",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                marginBottom: 14,
                                paddingBottom: 10,
                                borderBottom: `1px solid ${isDark ? "#2a2a2a" : "#eaedf1"}`,
                            }}
                        >
                            <UserOutlined style={{ color: "#1890ff", fontSize: 16 }} />
                            <div>
                                <Text strong style={{ fontSize: 13.5, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                    Dados do Chamado
                                </Text>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    Informações originais enviadas pelo cidadão
                                </Text>
                            </div>
                        </div>

                        <Descriptions
                            column={1}
                            size="small"
                            bordered
                            style={{
                                marginBottom: 14,
                                background: isDark ? "#141414" : "#f8fafc",
                            }}
                        >
                            <Descriptions.Item label="Usuário">
                                {contact?.app_user?.id ? (
                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <Tooltip title="Ver cadastro completo do usuário">
                                            <Button
                                                type="primary"
                                                size="small"
                                                icon={<UserOutlined style={{ fontSize: 11 }} />}
                                                onClick={() =>
                                                    navigate(`/usuarios/aplicativo/visualizar/${contact.app_user?.id}`)
                                                }
                                                style={{
                                                    backgroundColor: "#1677ff",
                                                    width: 22,
                                                    height: 22,
                                                    padding: 0,
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    borderRadius: 4,
                                                }}
                                            />
                                        </Tooltip>
                                        <span style={{ fontWeight: 600, fontSize: 12.5 }}>
                                            {contact.app_user.name}
                                        </span>
                                    </div>
                                ) : (
                                    <span style={{ fontSize: 12.5 }}>{contact?.app_user?.name || "--"}</span>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Assunto">
                                <Tag color="blue" style={{ margin: 0, fontSize: 11.5 }}>
                                    {contact?.subject_contact?.name || "Geral"}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Título">
                                <strong style={{ color: isDark ? "#f0f0f0" : "#1e293b", fontSize: 12.5 }}>
                                    {contact?.title || "--"}
                                </strong>
                            </Descriptions.Item>
                            <Descriptions.Item label="Abertura">
                                <span style={{ fontSize: 12 }}>
                                    {contact?.created_at
                                        ? formatDateStringToDateAndTime(contact.created_at)
                                        : "--"}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item label="Status">
                                {isOpen ? (
                                    <Tag color="warning" icon={<ClockCircleOutlined />} style={{ borderRadius: 4 }}>
                                        Aberto
                                    </Tag>
                                ) : (
                                    <Tag color="success" icon={<CheckCircleOutlined />} style={{ borderRadius: 4 }}>
                                        Fechado
                                    </Tag>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Atendente">
                                {contact?.panel_user?.name ? (
                                    <span style={{ fontWeight: 600, color: isDark ? "#93c5fd" : "#1d4ed8" }}>
                                        {contact.panel_user.name}
                                    </span>
                                ) : (
                                    <span style={{ color: isDark ? "#8c8c8c" : "#94a3b8" }}>Não atribuído</span>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Leitura Cidadão">
                                {contact?.app_user_viewed === false ? (
                                    <Tag color="warning" icon={<ClockCircleOutlined />} style={{ borderRadius: 4, fontWeight: 500, fontSize: 11.5 }}>
                                        Aguardando leitura
                                    </Tag>
                                ) : contact?.app_user_viewed && contact?.app_user_viewed_at ? (
                                    <Tooltip
                                        title={`Visualizado pelo cidadão em ${format(
                                            new Date(contact.app_user_viewed_at),
                                            "dd/MM/yyyy 'às' HH:mm",
                                            { locale: ptBR }
                                        )}`}
                                    >
                                        <span
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 4,
                                                color: isDark ? "#69b1ff" : "#0958d9",
                                                fontWeight: 500,
                                                fontSize: 12,
                                                cursor: "help",
                                            }}
                                        >
                                            <IoCheckmarkDone style={{ fontSize: 15 }} />
                                            Visto às {format(new Date(contact.app_user_viewed_at), "HH:mm")}
                                            <small style={{ color: isDark ? "#8c8c8c" : "#64748b", fontWeight: 400 }}>
                                                ({format(new Date(contact.app_user_viewed_at), "dd/MM/yyyy")})
                                            </small>
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <span style={{ color: isDark ? "#8c8c8c" : "#94a3b8", fontSize: 12 }}>--</span>
                                )}
                            </Descriptions.Item>
                        </Descriptions>
                    </div>
                </Col>
                <Col xs={24} lg={15}>
                    <div
                        style={{
                            background: isDark ? "#1a1a1a" : "#ffffff",
                            border: `1px solid ${isDark ? "#2a2a2a" : "#e2e8f0"}`,
                            boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.04)",
                            borderRadius: 10,
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                        }}
                    >
                        <div
                            style={{
                                padding: "12px 18px",
                                background: isDark ? "#141414" : "#f8fafc",
                                borderBottom: `1px solid ${isDark ? "#2a2a2a" : "#eaedf1"}`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <CustomerServiceOutlined style={{ color: "#52c41a", fontSize: 16 }} />
                                <div>
                                    <Text strong style={{ fontSize: 13.5, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                        Histórico de Mensagens ({allMessages.length})
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                        Comunicação e respostas enviadas pelo suporte
                                    </Text>
                                </div>
                            </div>

                            {contact?.app_user_viewed === false ? (
                                <Tag color="warning" icon={<ClockCircleOutlined />} style={{ borderRadius: 4, fontWeight: 500, fontSize: 11, margin: 0 }}>
                                    Aguardando leitura
                                </Tag>
                            ) : contact?.app_user_viewed && contact?.app_user_viewed_at ? (
                                <Tooltip
                                    title={`Visualizado pelo cidadão em ${format(
                                        new Date(contact.app_user_viewed_at),
                                        "dd/MM/yyyy 'às' HH:mm",
                                        { locale: ptBR }
                                    )}`}
                                >
                                    <span
                                        style={{
                                            fontSize: 11.5,
                                            color: isDark ? "#8696a0" : "#64748b",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 4,
                                            cursor: "help",
                                        }}
                                    >
                                        <IoCheckmarkDone style={{ color: "#1677ff", fontSize: 14 }} />
                                        Visto pelo cidadão às {format(new Date(contact.app_user_viewed_at), "HH:mm")}
                                    </span>
                                </Tooltip>
                            ) : null}
                        </div>

                        <div
                            ref={chatContainerRef}
                            style={{
                                flex: 1,
                                minHeight: 320,
                                maxHeight: 420,
                                overflowY: "auto",
                                padding: "16px 18px",
                                background: isDark ? "#0c1317" : "#efeae2",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            {allMessages.length > 0 ? (
                                allMessages.map((msg, idx) => (
                                    <CardMessageContact
                                        key={msg.id ? msg.id : `initial_${idx}`}
                                        message={msg}
                                        isInitial={msg.isInitial}
                                        handleDeleteMessage={handleDeleteMessage}
                                        handleOpenFile={handleOpenFile}
                                    />
                                ))
                            ) : (
                                <div style={{ margin: "auto", textAlign: "center", padding: "30px 0" }}>
                                    <Empty
                                        description="Nenhuma mensagem neste chamado."
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    />
                                </div>
                            )}
                        </div>

                        {formIsDisabled ? (
                            <div style={{ padding: 14, background: isDark ? "#141414" : "#f8fafc", borderTop: `1px solid ${isDark ? "#2a2a2a" : "#e2e8f0"}` }}>
                                <Alert
                                    type="info"
                                    message="Este chamado está fechado. Para responder novamente, altere o status para Aberto no topo da tela."
                                    showIcon
                                    style={{ borderRadius: 6 }}
                                />
                            </div>
                        ) : (
                            <div
                                style={{
                                    padding: "12px 16px",
                                    background: isDark ? "#141414" : "#ffffff",
                                    borderTop: `1px solid ${isDark ? "#2a2a2a" : "#e2e8f0"}`,
                                }}
                            >
                                <div style={{ marginBottom: 10 }}>
                                    <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 6 }}>
                                        Sugestões rápidas de resposta:
                                    </Text>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            flexWrap: "wrap",
                                            gap: "6px 8px",
                                        }}
                                    >
                                        {SUGGESTIONS_RESPONSES.map((suggestion, index) => (
                                            <CardSuggestionMessage
                                                key={index}
                                                text={suggestion.title}
                                                onClick={() => handleClickSuggestion(suggestion.message)}
                                            />
                                        ))}
                                    </div>
                                </div>
                                {attachedFiles.length > 0 && (
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            flexWrap: "wrap",
                                            gap: 8,
                                            padding: "8px 10px",
                                            marginBottom: 10,
                                            background: isDark ? "#202c33" : "#f1f5f9",
                                            border: `1px solid ${isDark ? "#2a3942" : "#cbd5e1"}`,
                                            borderRadius: 8,
                                        }}
                                    >
                                        <Text strong style={{ fontSize: 11, color: isDark ? "#8696a0" : "#64748b" }}>
                                            Anexos prontos para envio ({attachedFiles.length}/5):
                                        </Text>
                                        {attachedFiles.map((file) => {
                                            const isImg = file.type?.startsWith("image/") || file.name.match(/\.(png|jpe?g|webp)$/i);
                                            return (
                                                <div
                                                    key={file.uid}
                                                    style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: 6,
                                                        background: isDark ? "#141414" : "#ffffff",
                                                        border: `1px solid ${isDark ? "#333" : "#e2e8f0"}`,
                                                        borderRadius: 6,
                                                        padding: "3px 8px",
                                                        fontSize: 12,
                                                    }}
                                                >
                                                    {isImg ? (
                                                        <PictureOutlined style={{ color: "#1677ff" }} />
                                                    ) : (
                                                        <FilePdfOutlined style={{ color: "#ff4d4f" }} />
                                                    )}
                                                    <span
                                                        style={{
                                                            maxWidth: 140,
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {file.name}
                                                    </span>
                                                    <CloseCircleFilled
                                                        onClick={() => handleRemoveFile(file.uid)}
                                                        style={{
                                                            color: "#ff4d4f",
                                                            cursor: "pointer",
                                                            fontSize: 13,
                                                        }}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}


                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: "none" }}
                                    multiple
                                    accept="image/png, image/jpg, image/jpeg, image/webp, application/pdf"
                                    onChange={handleFileChange}
                                />
                                <Form
                                    onFinish={handleSubmit(handleSubmitForm)}
                                    autoComplete="off"
                                    layout="vertical"
                                >
                                    <div style={{ display: "flex", alignItems: "flex-end", gap: 8, paddingBottom: 20 }}>
                                        <Tooltip title="Anexar fotos ou PDF (PNG, JPG, WEBP, PDF)">
                                            <Button
                                                type="default"
                                                icon={<PaperClipOutlined style={{ fontSize: 17 }} />}
                                                onClick={() => fileInputRef.current?.click()}
                                                style={{
                                                    height: 38,
                                                    width: 38,
                                                    borderRadius: 8,
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    padding: 0,
                                                    flexShrink: 0,
                                                }}
                                            />
                                        </Tooltip>
                                        <div style={{ flex: 1 }}>
                                            <Controller
                                                name="message"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input.TextArea
                                                        {...field}
                                                        maxLength={255}
                                                        showCount
                                                        autoSize={{ minRows: 1, maxRows: 4 }}
                                                        placeholder="Digite uma resposta (máx. 255 caracteres)..."
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter" && !e.shiftKey) {
                                                                e.preventDefault();
                                                                if (hasContentToSend && !isSubmiting) {
                                                                    handleSubmit(handleSubmitForm)();
                                                                }
                                                            }
                                                        }}
                                                        style={{
                                                            borderRadius: 8,
                                                            padding: "8px 12px",
                                                            fontSize: 13,
                                                            resize: "none",
                                                        }}
                                                    />
                                                )}
                                            />
                                        </div>
                                        <Tooltip title="Enviar resposta (Enter)">
                                            <Button
                                                type="primary"
                                                icon={<SendOutlined style={{ fontSize: 16 }} />}
                                                htmlType="submit"
                                                loading={isSubmiting}
                                                disabled={!hasContentToSend || isSubmiting}
                                                style={{
                                                    height: 38,
                                                    width: 38,
                                                    borderRadius: "50%",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    padding: 0,
                                                    background: hasContentToSend ? "#00a884" : undefined,
                                                    borderColor: hasContentToSend ? "#00a884" : undefined,
                                                    flexShrink: 0,
                                                }}
                                            />
                                        </Tooltip>
                                    </div>

                                    {errors.message && (
                                        <div style={{ color: "#ff4d4f", fontSize: 11.5, marginTop: 4 }}>
                                            {errors.message.message}
                                        </div>
                                    )}
                                </Form>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            <Modal
                open={isDeleteModalVisible}
                onCancel={handleCancelDeleteMessage}
                onOk={handleConfirmDeleteMessage}
                confirmLoading={isLoadingDelete}
                okText="Sim, Deletar"
                cancelText="Cancelar"
                okType="danger"
                okButtonProps={{ loading: isLoadingDelete }}
                cancelButtonProps={{ disabled: isLoadingDelete }}
                centered
                title="Confirmar Exclusão da Resposta"
            >
                <Paragraph style={{ marginTop: 12 }}>
                    Deseja realmente deletar a resposta enviada?
                </Paragraph>
                <div
                    style={{
                        background: isDark ? "#141414" : "#fff1f0",
                        border: `1px solid ${isDark ? "#262626" : "#ffa39e"}`,
                        borderRadius: 6,
                        padding: "10px 14px",
                        fontSize: 12.5,
                    }}
                >
                    {contact?.messages?.find((m) => m.id === messageContactIdToDelete)?.message || "--"}
                </div>
            </Modal>
        </div>
    );
}
